import { useCallback, useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import centreService, { CentreCall } from '../services/centre.service';
import { CallRecorder, createCallRecorder } from '../utils/callRecording.utils';
import { unlockCentreNotifSound } from '../utils/centreNotifSound';
import { clearIncomingCallLink, peekIncomingCallLink } from '../utils/incomingCallLink';
import { bindSocketTokenRefresh } from '../utils/socketAuth';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL ||
  (process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace('/api/v1', '') : 'http://localhost:5000');

export type CallPhase = 'idle' | 'outgoing' | 'incoming' | 'connecting' | 'active' | 'ended';

export interface ActiveCallView {
  id: number;
  phone: string;
  name: string;
  direction: 'in' | 'out';
  phase: CallPhase;
  provider?: 'whatsapp' | 'voip';
  held?: boolean;
  detail?: string;
}

const waitIce = (pc: RTCPeerConnection, maxMs = 1200) => new Promise<void>((resolve) => {
  if (pc.iceGatheringState === 'complete') {
    resolve();
    return;
  }
  const done = () => {
    if (pc.iceGatheringState === 'complete') {
      pc.removeEventListener('icegatheringstatechange', done);
      resolve();
    }
  };
  pc.addEventListener('icegatheringstatechange', done);
  setTimeout(() => {
    pc.removeEventListener('icegatheringstatechange', done);
    resolve();
  }, maxMs);
});

const createPeer = async (onRemote: (stream: MediaStream) => void) => {
  const local = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
  const pc = new RTCPeerConnection({
    iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
    bundlePolicy: 'max-bundle',
    rtcpMuxPolicy: 'require'
  });
  local.getTracks().forEach((track) => pc.addTrack(track, local));
  pc.ontrack = (event) => {
    const stream = event.streams[0];
    if (stream) onRemote(stream);
  };
  return { pc, local };
};

interface UseWhatsAppCallArgs {
  storeId: number | null;
  accessToken: string | null;
  enabled: boolean;
  recordingEnabled?: boolean;
  onNeedPermission?: () => void;
  onPermissionReply?: (granted: boolean) => void;
  onError?: (message: string) => void;
}

export const useWhatsAppCall = ({
  storeId,
  accessToken,
  enabled,
  recordingEnabled = false,
  onNeedPermission,
  onPermissionReply,
  onError
}: UseWhatsAppCallArgs) => {
  const [view, setView] = useState<ActiveCallView | null>(null);
  const [muted, setMuted] = useState(false);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const localRef = useRef<MediaStream | null>(null);
  const remoteEl = useRef<HTMLAudioElement | null>(null);
  const incomingSdp = useRef<string | null>(null);
  const callIdRef = useRef<number | null>(null);
  const socketRef = useRef<Socket | null>(null);
  const answeringRef = useRef(false);
  const remoteStreamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<CallRecorder | null>(null);
  const activeStartedAt = useRef<number | null>(null);

  const cleanupMedia = useCallback(() => {
    pcRef.current?.getSenders().forEach((sender) => sender.track?.stop());
    pcRef.current?.close();
    pcRef.current = null;
    localRef.current?.getTracks().forEach((track) => track.stop());
    localRef.current = null;
    incomingSdp.current = null;
    remoteStreamRef.current = null;
    if (remoteEl.current) remoteEl.current.srcObject = null;
  }, []);

  const discardRecorder = useCallback(() => {
    const rec = recorderRef.current;
    recorderRef.current = null;
    activeStartedAt.current = null;
    if (rec) void rec.stop();
  }, []);

  const attachRemote = useCallback((stream: MediaStream) => {
    remoteStreamRef.current = stream;
    recorderRef.current?.addRemote(stream);
    if (!remoteEl.current) {
      remoteEl.current = new Audio();
      remoteEl.current.autoplay = true;
    }
    remoteEl.current.srcObject = stream;
    void remoteEl.current.play().catch(() => {});
  }, []);

  const reset = useCallback(() => {
    answeringRef.current = false;
    discardRecorder();
    cleanupMedia();
    callIdRef.current = null;
    setMuted(false);
    setView(null);
  }, [cleanupMedia, discardRecorder]);

  const toggleMute = useCallback(() => {
    const next = !muted;
    localRef.current?.getAudioTracks().forEach((track) => {
      track.enabled = !next;
    });
    setMuted(next);
  }, [muted]);

  const takeRecorderBlob = useCallback(() => {
    const rec = recorderRef.current;
    recorderRef.current = null;
    const started = activeStartedAt.current;
    activeStartedAt.current = null;
    return {
      started,
      blobPromise: rec ? rec.stop() : Promise.resolve(null as Blob | null)
    };
  }, []);

  const uploadBlob = useCallback(async (
    sid: number,
    callId: number,
    blob: Blob | null,
    started: number | null
  ) => {
    const durationSec = started ? Math.round((Date.now() - started) / 1000) : null;
    if (!blob || blob.size <= 800) return;
    let lastError: unknown;
    for (let attempt = 0; attempt < 4; attempt += 1) {
      try {
        await centreService.uploadCallRecording(sid, callId, blob, durationSec);
        return;
      } catch (error: any) {
        lastError = error;
        const code = error?.response?.data?.error?.code;
        if (code !== 'RECORDING_FORBIDDEN' || attempt === 3) break;
        await new Promise((resolve) => setTimeout(resolve, 400 * (attempt + 1)));
      }
    }
    throw lastError;
  }, []);

  const hangup = useCallback(async () => {
    const id = callIdRef.current;
    const sid = storeId;
    const { started, blobPromise } = takeRecorderBlob();
    answeringRef.current = false;
    cleanupMedia();
    callIdRef.current = null;
    setMuted(false);
    setView(null);
    if (id && sid) {
      try {
        await centreService.hangupCall(sid, id);
      } catch {
        /* already closed */
      }
      try {
        await uploadBlob(sid, id, await blobPromise, started);
      } catch {
        /* silent */
      }
      return;
    }
    void blobPromise;
  }, [cleanupMedia, storeId, takeRecorderBlob, uploadBlob]);

  const reject = useCallback(async () => {
    if (answeringRef.current) return;
    const id = callIdRef.current;
    const sid = storeId;
    reset();
    if (id && sid) {
      try {
        await centreService.rejectCall(sid, id);
      } catch {
        /* already closed */
      }
    }
  }, [reset, storeId]);

  const startCall = useCallback(async ({
    phone,
    name,
    orderId
  }: { phone: string; name?: string; orderId?: number | null }) => {
    if (!storeId || !phone) return;
    unlockCentreNotifSound();
    try {
      const { pc, local } = await createPeer(attachRemote);
      pcRef.current = pc;
      localRef.current = local;
      const offer = await pc.createOffer({ offerToReceiveAudio: true, offerToReceiveVideo: false });
      await pc.setLocalDescription(offer);
      await waitIce(pc, 2000);
      const sdp = pc.localDescription?.sdp;
      if (!sdp) throw new Error('SDP indisponible');

      setView({ id: 0, phone, name: name || phone, direction: 'out', phase: 'outgoing' });
      const result = await centreService.startCall(storeId, {
        phone,
        sdp,
        sdp_type: 'offer',
        contact_name: name,
        order_id: orderId
      });
      if (result.need_permission) {
        reset();
        onNeedPermission?.();
        return;
      }
      if (result.call) {
        callIdRef.current = result.call.id;
        setView({
          id: result.call.id,
          phone: result.call.phone,
          name: result.call.contact_name || name || phone,
          direction: 'out',
          phase: 'outgoing'
        });
      }
    } catch (error: any) {
      reset();
      onError?.(error?.response?.data?.error?.message || error.message || 'Appel impossible');
    }
  }, [attachRemote, onError, onNeedPermission, reset, storeId]);

  const accept = useCallback(async () => {
    if (!storeId || !callIdRef.current || !incomingSdp.current) return;
    if (answeringRef.current) return;
    answeringRef.current = true;
    unlockCentreNotifSound();
    setView((prev) => (prev ? { ...prev, phase: 'connecting' } : prev));

    const callId = callIdRef.current;
    try {
      // Lock the call immediately (parallel with mic) so other agents can't steal it.
      const claimPromise = centreService.claimCall(storeId, callId);
      const peerPromise = createPeer(attachRemote);

      await claimPromise;

      const { pc, local } = await peerPromise;
      pcRef.current = pc;
      localRef.current = local;
      await pc.setRemoteDescription({ type: 'offer', sdp: incomingSdp.current });
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      await waitIce(pc, 1200);
      const sdp = pc.localDescription?.sdp;
      if (!sdp) throw new Error('SDP indisponible');

      await centreService.acceptCall(storeId, callId, { sdp, sdp_type: 'answer' });
      setView((prev) => (prev ? { ...prev, phase: 'active' } : prev));
    } catch (error: any) {
      const code = error?.response?.data?.error?.code;
      const message = error?.response?.data?.error?.message || error.message || 'Décrochage impossible';
      reset();
      if (code === 'CALL_TAKEN') {
        onError?.('Appel pris par un autre agent');
        return;
      }
      onError?.(message);
    }
  }, [attachRemote, onError, reset, storeId]);

  useEffect(() => {
    if (!enabled || !storeId) return;
    const token = localStorage.getItem('accessToken') || accessToken;
    if (!token) return;

    const socket = io(SOCKET_URL, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5
    });
    bindSocketTokenRefresh(socket);
    socketRef.current = socket;

    socket.on('connect', () => {
      socket.emit('store:join', storeId);
    });

    socket.on('whatsapp:call', (payload: {
      type?: string;
      provider?: string;
      call?: CentreCall;
      sdp?: string;
      sdp_type?: string;
      granted?: boolean;
    }) => {
      const type = payload?.type;
      if (payload?.provider === 'voip') return;
      if (type === 'permission') {
        onPermissionReply?.(!!payload.granted);
        return;
      }
      const call = payload.call;
      if (type === 'incoming' && call) {
        if (answeringRef.current) return;
        callIdRef.current = call.id;
        incomingSdp.current = payload.sdp || call.sdp?.remote || null;
        setView({
          id: call.id,
          phone: call.phone,
          name: call.contact_name || call.phone,
          direction: 'in',
          phase: 'incoming'
        });
        return;
      }
      if (type === 'identity' && call) {
        setView((prev) => {
          if (!prev || prev.id !== call.id) return prev;
          const nextName = call.contact_name || prev.name;
          if (nextName === prev.name) return prev;
          return { ...prev, name: nextName };
        });
        return;
      }
      if (type === 'answer' && payload.sdp && pcRef.current) {
        void pcRef.current.setRemoteDescription({
          type: (payload.sdp_type as RTCSdpType) || 'answer',
          sdp: payload.sdp
        }).then(() => {
          setView((prev) => (prev ? { ...prev, phase: 'active' } : prev));
        }).catch(() => {});
        return;
      }
      if (type === 'status' && call?.status === 'active') {
        setView((prev) => (prev && prev.id === call.id ? { ...prev, phase: 'active' } : prev));
        return;
      }
      if (type === 'ended' && call) {
        if (callIdRef.current && callIdRef.current !== call.id) return;
        const id = callIdRef.current;
        const sid = storeId;
        const { started, blobPromise } = takeRecorderBlob();
        answeringRef.current = false;
        cleanupMedia();
        callIdRef.current = null;
        setMuted(false);
        setView(null);
        if (id && sid) {
          void (async () => {
            try {
              await centreService.hangupCall(sid, id);
            } catch {
              /* already closed */
            }
            try {
              await uploadBlob(sid, id, await blobPromise, started);
            } catch {
              /* silent */
            }
          })();
        } else {
          void blobPromise;
        }
        return;
      }
      if (type === 'taken' && call) {
        if (callIdRef.current && callIdRef.current !== call.id) return;
        if (answeringRef.current && callIdRef.current === call.id) return;
        reset();
      }
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [accessToken, cleanupMedia, enabled, onPermissionReply, reset, storeId, takeRecorderBlob, uploadBlob]);

  useEffect(() => {
    if (!enabled || !storeId) return;
    const link = peekIncomingCallLink();
    if (!link) return;
    if (link.provider === 'voip') return;
    if (link.storeId && link.storeId !== storeId) return;

    let cancelled = false;
    const restore = async () => {
      try {
        const call = await centreService.getCall(storeId, link.callId);
        if (cancelled) return;
        clearIncomingCallLink();
        if (!call || call.status !== 'ringing') {
          if (call?.status !== 'active' && call?.status !== 'connecting') {
            onError?.('Appel terminé');
          }
          return;
        }
        if (answeringRef.current && callIdRef.current === call.id) return;
        callIdRef.current = call.id;
        incomingSdp.current = call.sdp?.remote || null;
        setView({
          id: call.id,
          phone: call.phone,
          name: call.contact_name || call.phone,
          direction: 'in',
          phase: 'incoming'
        });
        if (link.action === 'answer') {
          window.setTimeout(() => { void accept(); }, 0);
        } else if (link.action === 'reject') {
          window.setTimeout(() => { void reject(); }, 0);
        }
      } catch {
        if (!cancelled) {
          clearIncomingCallLink();
          onError?.('Appel terminé');
        }
      }
    };
    void restore();
    return () => { cancelled = true; };
  }, [accept, enabled, onError, reject, storeId]);

  useEffect(() => {
    if (!recordingEnabled || view?.phase !== 'active' || !localRef.current) return;
    if (recorderRef.current) return;
    const rec = createCallRecorder(localRef.current);
    if (!rec) return;
    if (remoteStreamRef.current) rec.addRemote(remoteStreamRef.current);
    recorderRef.current = rec;
    activeStartedAt.current = Date.now();
  }, [recordingEnabled, view?.phase]);

  useEffect(() => () => {
    discardRecorder();
    cleanupMedia();
  }, [cleanupMedia, discardRecorder]);

  return { view, muted, startCall, accept, reject, hangup, toggleMute };
};
